import React, { useState, useRef, useEffect } from 'react';

/**
 * Render children only once this has been scrolled near the viewport.
 *
 * The supplier view has ~107 blocks but only three or four fit on screen, and
 * building every table up front cost 27,143 DOM nodes — which is what made
 * switching back from Spreadsheet take ~2s. Blocks now cost a header until you
 * approach them, taking that to 1,317 nodes.
 *
 * Once shown it stays shown: re-hiding on scroll-away would thrash the DOM and
 * discard any cell a user had part-edited.
 */
const MARGIN_PX = 700;

const LazyMount = ({ placeholderHeight = 200, children }) => {
    const ref = useRef(null);
    const [shown, setShown] = useState(false);

    useEffect(() => {
        if (shown) return undefined;
        const el = ref.current;
        if (!el) return undefined;

        /* Decide from geometry first. IntersectionObserver callbacks are
           throttled while a tab is backgrounded, so relying on it alone can
           leave a visible block empty; a rect check is synchronous and works
           whatever the tab is doing. */
        const nearViewport = () => {
            const r = el.getBoundingClientRect();
            return r.top < window.innerHeight + MARGIN_PX && r.bottom > -MARGIN_PX;
        };

        if (nearViewport() || typeof IntersectionObserver === 'undefined') {
            setShown(true);
            return undefined;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries.some((e) => e.isIntersecting)) {
                    setShown(true);
                    observer.disconnect();
                }
            },
            { rootMargin: `${MARGIN_PX}px` }
        );
        observer.observe(el);

        // Belt and braces for the throttled-tab case: a cheap rect check on
        // scroll, which fires even when observer callbacks are being deferred.
        const onScroll = () => {
            if (nearViewport()) {
                setShown(true);
                observer.disconnect();
                window.removeEventListener('scroll', onScroll);
            }
        };
        window.addEventListener('scroll', onScroll, { passive: true });

        return () => {
            observer.disconnect();
            window.removeEventListener('scroll', onScroll);
        };
    }, [shown]);

    return (
        <div ref={ref} style={shown ? undefined : { minHeight: placeholderHeight }}>
            {shown ? children : null}
        </div>
    );
};

export default LazyMount;
